import React, { useState } from "react";
import styled from "styled-components/macro";
import BrainstormNetworkTest from "./integrationTests/BrainstormNetworkTest";
import Auth from "../Auth";

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const TesterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const FormContainer = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const InputGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 100px;
`;

const Button = styled.button`
  background: #4a4a4a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background: #333;
  }
`;

const ResultContainer = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 4px;
  pre {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ItemCard = styled.div`
  padding: 10px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const BrainstormTester = () => {
  const [prompt, setPrompt] = useState("Things a young cop in Atlanta would say when venting to a friend");
  const [context, setContext] = useState("The character has a cynical view of police work");
  const [count, setCount] = useState(5);
  const [existingItems, setExistingItems] = useState("");
  const [rejectedItems, setRejectedItems] = useState("");
  const [starterItems, setStarterItems] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);

    try {
      // Parse multi-line inputs into arrays
      const items = existingItems.split("\n").filter((item) => item.trim().length > 0);
      const user_rejected = rejectedItems.split("\n").filter((item) => item.trim().length > 0);
      const user_starters = starterItems.split("\n").filter((item) => item.trim().length > 0);

      const requestBody = {
        prompt,
        context,
        n: parseInt(count, 10),
        items,
        user_rejected,
        user_starters,
      };

      const response = await Auth.fetchWithIdToken("/api/ai/brainstorm", requestBody);

      setResult(response);
    } catch (err) {
      setError(err.message || "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h1>Brainstorm Tester</h1>
      <p>Use this tool to test the Brainstorm API directly and debug any issues with follow-up requests.</p>

      <TesterContainer>
        <FormContainer>
          <h2>Manual API Tester</h2>
          <form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="prompt">Prompt</Label>
              <TextInput
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="context">Context</Label>
              <TextInput
                id="context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="count">Number of items to generate</Label>
              <TextInput
                id="count"
                type="number"
                min="1"
                max="10"
                value={count}
                onChange={(e) => setCount(e.target.value)}
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="existing">Existing Items (one per line)</Label>
              <TextArea
                id="existing"
                value={existingItems}
                onChange={(e) => setExistingItems(e.target.value)}
                placeholder="Items the user has kept (thumbs up)"
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="rejected">Rejected Items (one per line)</Label>
              <TextArea
                id="rejected"
                value={rejectedItems}
                onChange={(e) => setRejectedItems(e.target.value)}
                placeholder="Items the user has rejected (thumbs down)"
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="starters">Starter Items (one per line)</Label>
              <TextArea
                id="starters"
                value={starterItems}
                onChange={(e) => setStarterItems(e.target.value)}
                placeholder="Initial examples provided by the user"
              />
            </InputGroup>

            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? "Generating..." : "Run Brainstorm"}
            </Button>
          </form>

          {error && (
            <ResultContainer>
              <h3>Error</h3>
              <pre>{error}</pre>
            </ResultContainer>
          )}

          {result && (
            <ResultContainer>
              <h3>Generated Items</h3>
              {result.items?.length > 0 ? (
                <ItemsList>
                  {result.items.map((item, index) => (
                    <ItemCard key={index}>{item}</ItemCard>
                  ))}
                </ItemsList>
              ) : (
                <p>No items were generated.</p>
              )}

              <details>
                <summary>Full Response</summary>
                <pre>{JSON.stringify(result, null, 2)}</pre>
              </details>
            </ResultContainer>
          )}
        </FormContainer>

        <BrainstormNetworkTest />
      </TesterContainer>
    </Container>
  );
};

export default BrainstormTester;
